import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { PreviewImage } from "@/components/ui/image";
import { Typography } from "@castlery/fortress";
import { cn } from "@/lib/utils";
import { Currency, formatCurrency } from "@/utils";
import { useChatStore } from "@/stores/chatStore";
import { UtmUrlHelper } from "@/utils/utm";
import { externalFeatures } from "@/constant";
import { checkBeforeAddToCart } from "@/utils/cartHelper";
import { useToast } from "@/hooks/useToast";
import { Conversation } from "@/types";
import { apiPost } from "@/utils/apiHelper";
import { CARD_CART_API } from "@/config/api";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  LINK_TAG,
  trackEvent,
} from "@/utils/analytics";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";
import { AddToCart } from "./AddToCart";
import { useMessageContext } from "./MessageContext";

interface ProductData {
  currency: Currency;
  product_id: string;
  product_images: string[];
  product_link: string;
  product_name: string;
  product_original_price: number;
  product_pic: string;
  product_price: number;
  product_rating: number | null;
  product_rating_quantity: number | null;
  product_sku_code: string;
  variant_id: string;
  sale_quantity_min: number;
}

const DEFAULT_CARD_WIDTH = 160;
const PRODUCT_CARD_GAP = 8;

const calculateCardWidth = (containerWidth: number) => {
  return Math.max(
    0,
    ((containerWidth - PRODUCT_CARD_GAP * 2) * 6) / 13,
  );
};

const ProductCard = ({
  conversation,
  data,
  cardWidth,
}: {
  conversation: Conversation;
  data: ProductData;
  cardWidth: number;
}) => {
  const { toast } = useToast();
  const { message_id, handleProductClick } = useMessageContext();
  const [waitingForAddToCart, setWaitingForAddToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const utmLink = UtmUrlHelper.generateUtmUrl(
    data.product_link,
    data.product_name,
  );

  const productObj = {
    id: data.product_id,
  };
  const variantObj = {
    id: data.variant_id,
    price: data.product_price,
    variant_option_values: [],
  };

  // NOTE: 使用 product_images，如果为空则使用 product_pic 作为后备
  const images =
    data.product_images && data.product_images.length > 0
      ? data.product_images
      : [data.product_pic];

  const handleAddToCart = async () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "product_recommend",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "add_to_cart",
    });

    if (waitingForAddToCart) {
      return;
    }

    const quantity = 1;
    const canEnableAddToCart = checkBeforeAddToCart({
      product: productObj,
      variant: variantObj,
      quantity,
    });

    if (!canEnableAddToCart) {
      return;
    }

    setWaitingForAddToCart(true);
    try {
      const res = await externalFeatures.addToCart({
        product: productObj,
        variant: variantObj,
        quantity,
      });

      if (res) {
        toast({
          description: "Added to cart successfully",
        });
        await apiPost(CARD_CART_API.addRecord(), {
          conversation_id: conversation.conversation_id,
          message_id,
          product_name: data.product_name,
          product_sku_code: data.product_sku_code,
          quantity,
        });
      }

      // throw new Error("Add to cart failed");
    } catch (error) {
      console.error(error);
      // NOTE: 不需要 toast 提示，外部第三方会提示用户
      // NOTE: 抛出错误，让 ErrorBoundary 捕获
      // throw error;
    } finally {
      setWaitingForAddToCart(false);
    }
  };

  const handleProductNameClick = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "product_recommend",
      action: CLICK_ACTION,
      tag: LINK_TAG,
      tag_value: "product_link",
    });
    // 记录商品点击埋点（包含 message_id & sku）
    handleProductClick(data.product_sku_code);
  };

  return (
    <Card
      className="flex-shrink-0 flex flex-col items-center rounded-md border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E] overflow-hidden cursor-pointer"
      style={{ width: cardWidth }}
      data-testid="product-recommend-card"
      onClick={() => {
        handleProductNameClick();

        // 记录商品点击埋点（包含 message_id & sku）
        handleProductClick(data.product_sku_code);

        window.open(utmLink, "_blank");
      }}
    >
      <CardContent className="flex-1 flex flex-col p-0">
        {/* 商品图片 */}
        <div
          className="relative"
          style={{ width: cardWidth, height: cardWidth }}
        >
          <PreviewImage
            src={images[currentImageIndex]}
            alt={data.product_name}
            allowZoom={false}
          />
          {/* 图片指示器 */}
          {images.length > 1 && (
            <div
              className="absolute bottom-2 left-0 right-0 flex justify-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={cn(
                    "w-1.5 h-1.5 border rounded-full transition-all",
                    {
                      "bg-[#616161] border-[#616161]":
                        index === currentImageIndex,
                      "bg-transparent border-[#BEBEBE]":
                        index !== currentImageIndex,
                    },
                  )}
                  aria-label={`View the ${index + 1}th image`}
                />
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 flex flex-col px-2 py-3">
          <div className="mb-3 space-y-3">
            {/* 商品名称 */}
            <Typography className="text-[#3C101E]" level="body2">
              {data.product_name}
            </Typography>

            {/* 商品价格 */}
            <div>
              <div className="flex items-center gap-2">
                <Typography level="body2">
                  {formatCurrency(data.product_price, data.currency)}
                </Typography>
                {data.product_original_price !== data.product_price && (
                  <Typography
                    level="body2"
                    className="text-[#9E9E9E] line-through"
                  >
                    {formatCurrency(data.product_original_price, data.currency)}
                  </Typography>
                )}
              </div>
              {data.sale_quantity_min > 1 && (
                <Typography level="caption1" className="text-[#9E9E9E]">
                  (Set of {data.sale_quantity_min})
                </Typography>
              )}
            </div>
          </div>

          {/* 添加到购物车按钮 */}
          <div className="flex items-start">
            <AddToCart
              waitingForAddToCart={waitingForAddToCart}
              handleAddToCart={handleAddToCart}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ProductRecommendWithoutErrorBoundary = ({
  conversation,
  data,
}: {
  conversation: Conversation;
  data: ProductData[];
}) => {
  let products: ProductData[] = [];
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(DEFAULT_CARD_WIDTH);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const updateCardWidth = (containerWidth?: number) => {
      if (!containerWidth) {
        return;
      }

      setCardWidth(calculateCardWidth(containerWidth));
    };

    updateCardWidth(container.clientWidth);

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      updateCardWidth(entry?.contentRect.width);
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  if (Array.isArray(data)) {
    products = data;
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 max-w-full text-xs text-[#3C101E]"
      data-testid="product-recommend-container"
    >
      <div className="flex flex-row space-x-2 overflow-x-auto">
        {products?.map((product: ProductData) => (
          <ProductCard
            key={`${product.product_id}_${product.variant_id}`}
            conversation={conversation}
            data={product}
            cardWidth={cardWidth}
          />
        ))}
      </div>
    </div>
  );
};

export const ProductRecommend = ({
  conversation,
  data,
}: {
  conversation: Conversation;
  data: ProductData[];
}) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <ProductRecommendWithoutErrorBoundary
        conversation={conversation}
        data={data}
      />
    </ErrorBoundary>
  );
};
